import React, { useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Translate } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LANGS = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
];

/**
 * Compact language switcher with two presentation modes.
 *
 * Props:
 *   variant: 'inline' (pill chips, used in Settings) | 'compact' (single dropdown button, used in topbar)
 *   className: extra classes
 *
 * Side effects:
 *   - i18n.changeLanguage (updates UI immediately + persists to localStorage via the detector cache)
 *   - PATCH /api/account/locale (silent best-effort sync to MongoDB so emails respect the choice)
 */
export default function LanguageSwitcher({ variant = 'inline', className = '' }) {
  const { i18n, t } = useTranslation();
  const current = (i18n.resolvedLanguage || i18n.language || 'en').slice(0, 2);

  const change = useCallback(async (code) => {
    if (code === current) return;
    await i18n.changeLanguage(code);
    try {
      await axios.patch(`${BACKEND_URL}/api/account/locale`, { locale: code }, { withCredentials: true });
    } catch {
      // Server sync is best-effort: the UI already updated via i18n + localStorage.
    }
  }, [current, i18n]);

  if (variant === 'compact') {
    return (
      <div className={`relative inline-block ${className}`} data-testid="language-switcher">
        <select
          value={current}
          onChange={(e) => change(e.target.value)}
          data-testid="lang-select-compact"
          className="appearance-none pl-7 pr-7 py-1.5 bg-[#0F0F0F] hover:bg-[#1A1A1A] border border-white/10 rounded-lg text-xs font-medium text-white/80 focus:outline-none focus:border-white/30 cursor-pointer"
        >
          {LANGS.map((l) => (
            <option key={l.code} value={l.code}>{l.flag} {l.label}</option>
          ))}
        </select>
        <Translate size={11} weight="bold" className="absolute left-2 top-1/2 -translate-y-1/2 text-white/50 pointer-events-none" />
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid="language-switcher">
      {LANGS.map((l) => {
        const active = l.code === current;
        return (
          <button
            key={l.code}
            data-testid={`lang-btn-${l.code}`}
            data-active={active ? 'true' : 'false'}
            onClick={() => change(l.code)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition inline-flex items-center gap-1.5 ${
              active
                ? 'bg-[#2563EB]/15 border-[#2563EB]/40 text-[#2563EB]'
                : 'bg-transparent border-white/10 text-white/55 hover:text-white hover:bg-white/5'
            }`}
            aria-pressed={active}
            aria-label={t('common.language') + ': ' + l.label}
          >
            <span>{l.flag}</span> {l.label}
          </button>
        );
      })}
    </div>
  );
}
